/**
 * plasmaGlobePipeline — glass sphere with plasma tendrils reaching
 * from the center to the surface.
 *
 * Composition:
 *   1. Background — deep purple plasma room with FBM haze.
 *   2. Glass sphere — refraction rim, semi-transparent body.
 *   3. Inner core — bright white-purple plasma ball.
 *   4. Tendrils — 18 electric tendrils branching from center to
 *      random points on the inner sphere surface, with wiggling
 *      noise displacement along their length.
 *   5. Surface sparks — bright dots where tendrils touch the surface.
 *   6. Ambient flicker — high-frequency noise on the rim.
 *
 * Audio reactivity:
 *   bass — pushes tendril length out, brightens core
 *   mid  — increases wiggle amplitude, rotates tendrils
 *   high — flickers sparks and adds crackle on the rim
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PlasmaGlobePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float h11(float n) {
  return fract(sin(n * 91.345) * 47453.12);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float r = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    r += a * noise2(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return r;
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 center = vec2(0.0, 0.0);
  float sphereR = 0.55;

  // --- 1. Background ---
  float bgT = clamp((p.y + 0.8) / 1.6, 0.0, 1.0);
  vec3 bg = mix(vec3(0.02, 0.01, 0.06), vec3(0.10, 0.04, 0.18), bgT);
  bg += vec3(0.30, 0.10, 0.40) * fbm(p * 3.0 + vec2(uTime * 0.05, uTime * 0.04)) * 0.4;

  vec3 col = bg;

  // --- 2. Tendrils (drawn behind/inside sphere via SDF feel) ---
  // Distance to globe center.
  float dCenter = length(p - center);

  // We sample 18 tendrils. Each tendril goes from center toward a point
  // on the surface (rotated by its phase).
  float tendrilTotal = 0.0;
  vec3 tendrilCol = vec3(0.0);
  for (int i = 0; i < 18; i++) {
    float fi = float(i);
    // Each tendril has a stable endpoint on the sphere.
    float baseAng = h11(fi * 1.7) * 6.2831;
    float tilt = (h11(fi * 2.3) - 0.5) * 1.4;
    vec3 surfPt = vec3(
      cos(baseAng) * cos(tilt),
      sin(tilt),
      sin(baseAng) * cos(tilt)
    );
    // Project to 2D — apply a rotation about y for parallax.
    float rotY = uTime * (0.3 + 0.1 * fi) + mid * 1.5;
    float cr = cos(rotY), sr = sin(rotY);
    vec3 sp = vec3(cr * surfPt.x + sr * surfPt.z, surfPt.y, -sr * surfPt.x + cr * surfPt.z);
    vec2 end2D = center + sp.xy * (sphereR * (0.85 + bass * 0.15));
    // Wiggle length varies with bass.
    float len = sphereR * (0.7 + bass * 0.4 + 0.15 * sin(uTime * (2.0 + fi * 0.5)));

    // Sample the tendril as a polyline.
    float minD = 1000.0;
    for (int s = 0; s < 14; s++) {
      float fs = float(s) / 13.0;
      vec2 pos = mix(center, end2D, fs);
      // Noise wiggle perpendicular to line.
      vec2 dir = normalize(end2D - center + 1e-4);
      vec2 perp = vec2(-dir.y, dir.x);
      float wig = noise2(vec2(fs * 8.0 + fi, uTime * 3.0 + fi * 0.7)) - 0.5;
      pos += perp * wig * 0.08 * mid;
      // Tangential noise along length.
      float tangWig = noise2(vec2(fs * 14.0 + fi * 3.0, uTime * 5.0)) - 0.5;
      pos += dir * tangWig * 0.02;
      float d = length(p - pos);
      if (d < minD) minD = d;
    }
    float tg = exp(-minD * 250.0);
    float halo = exp(-minD * 30.0) * 0.18;
    float intensity = 0.5 + bass * 0.6 + mid * 0.3 + high * 0.2;
    tendrilTotal += (tg + halo) * intensity;
    tendrilCol += vec3(0.45, 0.6, 1.0) * (tg + halo) * intensity;
    // Hot white core near sphere surface.
    float sparkDist = length(p - end2D);
    float spark = exp(-sparkDist * 250.0);
    tendrilCol += vec3(1.0, 1.0, 1.0) * spark * (0.4 + high * 1.5);
  }
  col += tendrilCol;

  // --- 3. Inner core (bright plasma ball) ---
  float coreR = 0.12 + bass * 0.03;
  float coreD = dCenter;
  float core = exp(-coreD * 12.0);
  float coreCore = smoothstep(coreR, 0.0, coreD);
  col += vec3(1.0, 0.95, 1.0) * coreCore * (2.0 + bass * 2.5);
  col += vec3(0.7, 0.5, 1.0) * core * (0.6 + mid * 0.5);

  // Flickering surface on the core.
  float coreFlicker = noise2(p * 40.0 + uTime * 6.0);
  col += vec3(0.9, 0.7, 1.0) * coreFlicker * core * (0.2 + high * 0.4);

  // --- 4. Glass sphere rim & shell ---
  float shellInner = sphereR - 0.005;
  float shellOuter = sphereR + 0.005;
  // Fresnel-like rim — strongest at grazing angles.
  float rimT = smoothstep(shellInner, shellOuter, dCenter) * (1.0 - smoothstep(shellOuter, shellOuter + 0.04, dCenter));
  float fres = pow(1.0 - clamp(dCenter / sphereR, 0.0, 1.0), 1.5);
  // Edge highlight.
  float edgeHL = exp(-pow(dCenter - sphereR, 2.0) * 800.0);
  col += vec3(0.5, 0.7, 1.0) * edgeHL * (0.8 + bass * 1.0);
  // Glass body — slight tint.
  float inside = smoothstep(sphereR, 0.0, dCenter);
  vec3 glassTint = vec3(0.10, 0.08, 0.30) * inside * 0.15;
  col = mix(col, col + glassTint, 0.5);

  // Outer glow halo around sphere.
  float outerHalo = exp(-pow(max(0.0, dCenter - sphereR), 1.0) * 4.0) * 0.6;
  col += vec3(0.4, 0.3, 0.9) * outerHalo * (0.3 + bass * 0.6);

  // --- 5. Crackle noise around the surface ---
  float crackle = noise2(p * 50.0 + uTime * 9.0);
  crackle = smoothstep(0.78, 0.95, crackle);
  float ring = exp(-pow(dCenter - sphereR, 2.0) * 200.0);
  col += vec3(0.6, 0.8, 1.0) * crackle * ring * 0.8;

  // --- 6. Outer ambient embers ---
  for (int i = 0; i < 20; i++) {
    float fi = float(i);
    float seed = h11(fi * 3.1 + 19.0);
    float t = uTime * (0.3 + seed * 0.5) + seed * 10.0;
    vec2 embPos = vec2(
      sin(t * 0.7) * (0.9 + 0.2 * seed),
      cos(t * 0.5 + fi) * (0.6 + 0.2 * seed)
    );
    float embD = length(p - embPos);
    float emb = exp(-embD * 200.0);
    col += vec3(0.6, 0.4, 0.9) * emb * (0.3 + high * 0.4);
  }

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.3;

  // Tone mapping + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computePlasmaGlobeBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 1.4);
  out[1] = 0.5 + 0.4 * Math.abs(Math.sin(t * 2.2));
  out[2] = 0.4 + 0.4 * Math.sin(t * 3.0 + 0.7);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.8 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 2.5 + 2.3);
  out[5] = 0.4 + 0.5 * Math.sin(t * 3.3 + 3.1);
  out[6] = 0.3 + 0.6 * Math.sin(t * 5.0 + 0.5) * Math.max(0, Math.sin(t * 0.3));
  out[7] = 0.4 + 0.5 * Math.sin(t * 7.0 + 1.7);
  out[8] = 0.3 + 0.6 * Math.sin(t * 9.5 + 2.1);
}

export function computePlasmaGlobeRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.012], [0.012, 0.040], [0.040, 0.080],
    [0.080, 0.180], [0.180, 0.300], [0.300, 0.460],
    [0.460, 0.640], [0.640, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0;
    let count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createPlasmaGlobePipeline(gl: WebGL2RenderingContext): PlasmaGlobePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    bands: new Float32Array(9),
  };
}

export function drawPlasmaGlobe(
  gl: WebGL2RenderingContext,
  pipe: PlasmaGlobePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}